import {
    ActionCreator, AjaxDispatcher
} from './ajax-dispatcher';
import { addClass, addEventListenerIfNotExists, getApiUrl, getCurrentLanguageId, getPopup, hide, loadAsyncComponent, openComponentInPopup, QueryParameter, removeClass, show } from './common';
import ConfirmDialog from './confirm-dialog';
import HttpClient from './http-client';
import { initCart, initDiscountCoupons, initDiscountCouponsList } from './main';
import OrderMatrixLink from './order-matrix-link';
import { PopupOptions, PopupWidth } from './popup';
import ProductCount from './product-count';
import QuantityEdit from './quantity-edit';
import SharedCartDialog from './shared-cart-dialog';
import TronCalculator from './tron-calculator';

interface UpdateGroupCartItem {
    groupCartItemId: string;
    cartItems: ProductCount[];
}

interface ChangeProductCartItemQuantity {
    cartItemId: string;
    quantity: number;
}

interface CartItem {
    id: string;
    removeButton: HTMLButtonElement;
}

interface ProductCartItem extends CartItem {
    quantityEdit: QuantityEdit;
}

interface GroupCartItem extends CartItem {
    updateButtons: HTMLElement[];
}

interface CalculationCartItem extends GroupCartItem {
    calculationId: string;
}

interface AddPrintingGroupData {
    items: ProductCount[],
    positionId?: string,
    technologyId?: string,
    printDescription: string,
    isSingleProduct: boolean,
    tronLogoGuid: string
}

export default class Cart {
    private products: ProductCartItem[];
    private groups: GroupCartItem[];
    private calculations: CalculationCartItem[];
    private shareButton: HTMLInputElement;
    private hasErrors = false;

    init(): void {
        const baseElement = document.querySelector('[data-component="Cart"]') as HTMLElement;
        this.hasErrors = baseElement.dataset.hasErrors === 'true';

        this.products = this.createProductCartItems(
            baseElement.querySelectorAll('div[data-cart-item-type="Product"]')
        );
        this.products = this.products.concat(this.createProductCartItems(
            baseElement.querySelectorAll('div[data-cart-item-type="Group_ProductSet"]')
        ));
        this.groups = this.createGroupCartItems(
            baseElement.querySelectorAll('div[data-cart-item-type="Group_ModelVariants"]')
        );

        this.calculations = this.createCalculationCartItems(
            baseElement.querySelectorAll('div[data-cart-item-type="Group_Calculation"]')
        );

        this.registerProductCartItemListeners();
        this.registerGroupCartItemListeners();
        this.registerCalculationCartItemListeners();
        this.initStockVerification(baseElement.querySelector('div[data-component="CartStockVerification"]'));
    }

    private async initStockVerification(element: HTMLElement): Promise<void> {
        if (!element) return;

        const loadedElement = await loadAsyncComponent(element);
        const isStockValid = loadedElement.dataset.stockValid === 'true';

        isStockValid && !this.hasErrors
            ? this.enableContinue(loadedElement.dataset.continueUrl)
            : this.disableContinue();
        
        const icon = this.getBaseElement().querySelector('[data-component-part="continue-loading-icon"]') as HTMLElement;
        icon && hide(icon);
        const label = this.getBaseElement().querySelector('[data-component-part="continue-button-label"]') as HTMLElement;
        label && show(label);
        
        isStockValid && await this.reloadTabNavigation();
    }

    private async reloadTabNavigation(): Promise<void> {
        const tabNavigation = this.getBaseElement().querySelector('[data-component="TabNavigation"]') as HTMLElement;
        await tabNavigation && loadAsyncComponent(tabNavigation);
    }

    enableContinue(url: string) {
        const baseElement = this.getBaseElement();
        const continueButton = baseElement.querySelector('[data-component-part="continue-button"]') as HTMLAnchorElement;
        continueButton.removeAttribute('data-disabled');
        if (url) {
            continueButton.href = url;
        }
        removeClass(continueButton, 'disabled');
    }

    private disableContinue(): void {
        const baseElement = this.getBaseElement();
        const continueButton = baseElement.querySelector('[data-component-part="continue-button"]') as HTMLAnchorElement;
        continueButton.setAttribute('data-disabled', 'true');
        const url = continueButton.href;
        if (url) {
            continueButton.removeAttribute('href');
        }
        addClass(continueButton, 'disabled');
    }

    initForCartSharing(shareButtonId: string): void {
        this.initShareCartElements(shareButtonId);
        this.registerShareCartListeners();
    }

    private initShareCartElements(shareButtonId: string): void {
        this.shareButton = document.getElementById(shareButtonId) as HTMLInputElement;
    }

    private registerShareCartListeners(): void {
        addEventListenerIfNotExists(this.shareButton, 'click', async () => {
            await this.openShareConfirmDialog();
        });
    }

    private async openShareConfirmDialog(): Promise<void> {
        const confirmDialog = new ConfirmDialog(async () => {
            await this.shareCart();
        });

        const queryParam: QueryParameter = {
            key: 'confirmDialogViewName',
            value: 'ConfirmShareCart'
        };

        confirmDialog.open([queryParam]);
    }

    private async shareCart(): Promise<void> {

        const url = `${this.getSharedCartApiUrl()}/create`;
        const languageId = getCurrentLanguageId();
        const response = await new HttpClient().fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': languageId
            },
        });

        if (response && response.ok) {
            this.openShareDialog(await response.text());
        }
    }

    private async openShareDialog(sharedCartGuid: string): Promise<void> {
        const queryParam: QueryParameter = {
            key: 'guid',
            value: `${sharedCartGuid}`
        };
        await openComponentInPopup('SharedCartDialog', [queryParam], { id: 'share-cart-dialog', width: PopupWidth.Small });

        const dialog = new SharedCartDialog();
        dialog.init("copySharedCartUrlButton", "sharedCartUrlInput");
    }

    async addProduct(productCounts: ProductCount[]): Promise<void> {
        if (!this.validate(productCounts)) {
            return;
        }

        if (productCounts.length === 1) {
            await this.addProductToCart(productCounts[0]);
        }
        else {
            await this.addProductsToCart(productCounts);
        }
    }

    async addSingleProductPrintingGroup(productCount: ProductCount, demandText: string, positionId: string = null, technologyId: string = null): Promise<void> {
        const requestData: AddPrintingGroupData = {
            items: [ productCount ],
            positionId: positionId,
            technologyId: technologyId,
            printDescription: demandText,
            isSingleProduct: true,
            tronLogoGuid: this.getTronLogoGuid()
        };
        await this.addPritingGroupInternal(requestData);
    }

    private getTronLogoGuid(): string {
        return window?.tron?.User?.getUserLogo() ?? null;
    }

    async addPrintingGroup(productCounts: ProductCount[], demandText: string, positionId: string = null, technologyId: string = null): Promise<void> {
        if (!this.validate(productCounts)) return;
        const requestData: AddPrintingGroupData = {
            items: productCounts,
            positionId: positionId,
            technologyId: technologyId,
            printDescription: demandText,
            isSingleProduct: false,
            tronLogoGuid: this.getTronLogoGuid()
        };
        await this.addPritingGroupInternal(requestData);
    }

    private async addPritingGroupInternal(requestData: AddPrintingGroupData){
        const response = await new HttpClient().fetch(`/api/cart/printing`, {
            method: 'POST',
            body: JSON.stringify(requestData),
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (response.ok) {
            this.reloadCartHeaderIcon();
        }
    }

    public getBaseElement(): HTMLElement {
        return document.querySelector('[data-component="Cart"]');
    }

    private validate(productCounts: ProductCount[]): boolean {
        if (productCounts.length === 0) return false;

        const productsWithZeroQuantity = productCounts.filter(p => p.quantity === 0);
        if(productsWithZeroQuantity.length === 0) return true;
    }

    private async addProductToCart(productCount: ProductCount): Promise<void> {
        const response = await new HttpClient().fetch(`/api/cart/product`, {
            method: 'POST',
            body: JSON.stringify(productCount),
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (response.ok) {
            this.reloadCartHeaderIcon();
        }
    }

    private async addProductsToCart(productCounts: ProductCount[]): Promise<void> {
        const response = await new HttpClient().fetch(`/api/cart/products`, {
            method: 'POST',
            body: JSON.stringify(productCounts),
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (response.ok) {
            this.reloadCartHeaderIcon();
        }
    }

    public async openCartDialog(productCounts: ProductCount[]): Promise<void> {
        const queryParam: QueryParameter = {
            key: 'product-counts',
            value: this.buildProductCountQueryString(productCounts)
        };
        await openComponentInPopup('CartDialog', [queryParam], { id: 'product-added-to-cart-popup', width: PopupWidth.Large }, true);
    }

    private buildProductCountQueryString(productCounts: ProductCount[]): string {
        return productCounts.map(i => i.productId + '-' + i.quantity).join(';');
    }

    private registerProductCartItemListeners(): void {
        this.registerRemoveItemListener(this.products);
    }

    private registerGroupCartItemListeners(): void {
        this.registerRemoveItemListener(this.groups);
        this.initOrderMatrixLinks();
    }

    private registerCalculationCartItemListeners(): void {
        this.registerRemoveItemListener(this.calculations);
        this.calculations.forEach(c => 
            c.updateButtons.forEach(b => b.addEventListener('click', async () => this.enterCalculator(c.calculationId)))
        );
    }

    private registerRemoveItemListener(cartItems: CartItem[]): void {
        cartItems.forEach(item => {
            item.removeButton &&
                addEventListenerIfNotExists(
                    item.removeButton,
                    'click',
                    async () => await this.removeCartItem(item.id)
                );
        });
    }

    private initOrderMatrixLinks(): void {
        this.groups.forEach(group => group.updateButtons.forEach(async b => {
            const link = new OrderMatrixLink(
                b,
                async (productCounts: ProductCount[]) => {
                    await this.updateGroupCartItem({
                        groupCartItemId: group.id,
                        cartItems: productCounts
                    });
                },
                null
            );

            await link.init(group.id);
        }));
    }

    private async enterCalculator(calculationGuid: string): Promise<void> {
        const calculator = new TronCalculator();
        await calculator.openExistingCalculation(calculationGuid);
    }

    private createProductCartItems(inputElements: NodeList): ProductCartItem[] {
        const array: HTMLInputElement[] = Array.prototype.slice.call(
            inputElements
        );

        const productCartItems: ProductCartItem[] = array.map(item => {
            return {
                id: item.getAttribute('data-product-id'),
                quantityEdit: new QuantityEdit(item.querySelector('[data-component-part="quantity-edit"]'), 
                    number => this.handleChangeQuantity(item.getAttribute('data-product-id'), number)),
                removeButton: item.querySelector('[data-action="remove"]')
            } as ProductCartItem;
        });

        return productCartItems;
    }

    private createGroupCartItems(inputElements: NodeList) {
        const array: HTMLInputElement[] = Array.prototype.slice.call(
            inputElements
        );

        return array.map(item => {
            return {
                id: item.getAttribute('data-product-id'),
                removeButton: item.querySelector('[data-action="remove"]'),
                updateButtons: Array.prototype.slice.call(item.querySelectorAll(
                    '[data-action="change-quantity"]'
                )).map(x => x as HTMLElement)
            } as GroupCartItem;
        });
    }

    private createCalculationCartItems(inputElements: NodeList) {
        const array: HTMLInputElement[] = Array.prototype.slice.call(
            inputElements
        );

        return array.map(item => {
            return {
                id: item.getAttribute('data-product-id'),
                calculationId: item.getAttribute('data-calculation-id'),
                removeButton: item.querySelector('[data-action="remove"]'),
                updateButtons: Array.prototype.slice.call(item.querySelectorAll(
                    '[data-action="change-quantity"]'
                )).map(x => x as HTMLElement)
            } as CalculationCartItem;
        });
    }

    private async handleChangeQuantity(
        cartItemId: string,
        quantity: number
    ): Promise<void> {
        quantity > 0 
            ? await this.changeProductQuantity({ cartItemId, quantity })
            : new ConfirmDialog(
                    async () => await this.removeCartItem(cartItemId),
                    () => this.reloadPage())
                .open();
        
    }

    private async changeProductQuantity(
        changeProductQuantityData: ChangeProductCartItemQuantity
    ): Promise<void> {
        const url = `${this.getApiUrl()}/product`;
        const languageId = getCurrentLanguageId();
        const response = await new HttpClient().fetch(url, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': languageId
            },
            body: JSON.stringify(changeProductQuantityData)
        });

        if (response && response.ok) {
            this.reloadPage();
        }
    }

    private async updateGroupCartItem(
        updateGroup: UpdateGroupCartItem
    ): Promise<void> {
        const url = '/api/cart/products';
        const response = await new HttpClient().fetch(url, {
            method: 'PUT',
            body: JSON.stringify(updateGroup),
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (response.ok) {
            this.reloadPage();
            this.closePopup();
        }
    }

    private closePopup(): void {
        const popup = getPopup(<PopupOptions>{ id: 'order-matrix-popup' });
        popup.close();
    }

    private async removeCartItem(itemId: string): Promise<void> {
        const url = `${this.getApiUrl()}?cartItemId=${itemId}`;
        const response = await new HttpClient().fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (response.ok) {
            this.reloadPage();
        }
    }

    private reloadCartHeaderIcon(): void {
        const action = new ActionCreator().reloadComponent('CartHeaderIcon');
        new AjaxDispatcher().dispatchGetAction(action);
    }

    public reloadPage(): void {

        const action = new ActionCreator().reloadComponents([
            'Cart',
            'CartHeaderIcon',
            'DiscountCoupons',
            'DiscountCouponsList'
        ]);
        // action.sucessAction = () => {
        //     initCart();
        //     initDiscountCoupons();
        //     initDiscountCouponsList();
        // };

        new AjaxDispatcher().dispatchGetAction(action);
    }

    private getApiUrl(): string {
        return `${getApiUrl()}/cart`;
    }

    private getSharedCartApiUrl(): string {
        return `${getApiUrl()}/sharedcart`;
    }
}
